import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import {
  Package, Plus, Image, Upload, Trash2, Edit, X, Camera,
  ArrowLeft, Save, Loader2
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PartnerProducts({ user, onLogout }) {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadedImages, setUploadedImages] = useState([]);

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    price: "",
    category: "boutique",
    stock: "",
    image_url: "",
    images: []
  });

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/products?vendor_id=${user.id}`);
      setProducts(response.data);
    } catch (error) {
      console.error("Error fetching products:", error);
      toast.error("Erreur lors du chargement des produits");
    } finally {
      setIsLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploadingImage(true);
    const newImages = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      // Validate file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`${file.name} est trop volumineux (max 5MB)`);
        continue;
      }

      // Validate file type
      if (!file.type.startsWith("image/")) {
        toast.error(`${file.name} n'est pas une image valide`);
        continue;
      }

      try {
        const formDataUpload = new FormData();
        formDataUpload.append("file", file);
        formDataUpload.append("vendor_id", user.id);

        const response = await axios.post(`${API}/products/upload-image`, formDataUpload, {
          headers: { "Content-Type": "multipart/form-data" }
        });

        if (response.data.success) {
          const fullUrl = `${BACKEND_URL}${response.data.image_url}`;
          newImages.push(fullUrl);
          toast.success(`${file.name} téléchargée`);
        }
      } catch (error) {
        console.error("Upload error:", error);
        toast.error(`Erreur lors du téléchargement de ${file.name}`);
      }
    }

    setUploadedImages(prev => [...prev, ...newImages]);
    setFormData(prev => ({
      ...prev,
      images: [...prev.images, ...newImages],
      image_url: prev.image_url || newImages[0] || ""
    }));

    setUploadingImage(false);
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const removeImage = (indexToRemove) => {
    setUploadedImages(prev => prev.filter((_, idx) => idx !== indexToRemove));
    setFormData(prev => {
      const newImages = prev.images.filter((_, idx) => idx !== indexToRemove);
      return {
        ...prev,
        images: newImages,
        image_url: newImages[0] || ""
      };
    });
  };

  const setMainImage = (imageUrl) => {
    setFormData(prev => ({ ...prev, image_url: imageUrl }));
    toast.success("Image principale définie");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const productData = {
        ...formData,
        price: parseFloat(formData.price) || 0,
        stock: parseInt(formData.stock) || 0,
        vendor_id: user.id
      };

      if (editingProduct) {
        await axios.put(`${API}/products/${editingProduct.id}`, productData);
        toast.success("Produit mis à jour!");
      } else {
        await axios.post(`${API}/products`, productData);
        toast.success("Produit ajouté!");
      }

      resetForm();
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      description: product.description,
      price: product.price.toString(),
      category: product.category,
      stock: product.stock.toString(),
      image_url: product.image_url || "",
      images: product.images || []
    });
    setUploadedImages(product.images || []);
    setShowForm(true);
  };

  const handleDelete = async (productId) => {
    if (!window.confirm("Supprimer ce produit ?")) return;

    try {
      await axios.delete(`${API}/products/${productId}`);
      toast.success("Produit supprimé");
      fetchProducts();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      price: "",
      category: "boutique",
      stock: "",
      image_url: "",
      images: []
    });
    setUploadedImages([]);
    setEditingProduct(null);
    setShowForm(false);
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* Header */}
      <nav className="bg-gradient-to-r from-green-600 to-green-700 text-white px-4 md:px-8 py-4 shadow-lg">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-3">
            <img
              src="/woma-logo.png"
              alt="ARTEKNOS Logo"
              className="h-11 w-11 rounded-full object-cover ring-2 ring-[#D4AF37]/40 shadow-lg shadow-[#D4AF37]/20"
            />
            <div>
              <h1 className="text-xl font-bold" style={{fontFamily: 'Outfit, sans-serif'}}>
                Mes Produits
              </h1>
              <p className="text-xs text-green-100">{user.business_name || user.full_name}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button
              onClick={() => navigate('/partner')}
              variant="outline"
              className="rounded-full text-white border-white hover:bg-white hover:text-green-700"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour
            </Button>
            <Button
              onClick={onLogout}
              variant="outline"
              className="rounded-full text-white border-white hover:bg-white hover:text-green-700"
            >
              Déconnexion
            </Button>
          </div>
        </div>
      </nav>

      <div className="px-4 md:px-8 py-8">
        <div className="max-w-6xl mx-auto">
          {/* Add Product Button */}
          {!showForm && (
            <div className="mb-6">
              <Button
                onClick={() => setShowForm(true)}
                className="bg-green-600 hover:bg-green-700 rounded-full px-6"
                data-testid="add-product-btn"
              >
                <Plus className="w-5 h-5 mr-2" />
                Ajouter un produit
              </Button>
            </div>
          )}

          {/* Product Form */}
          {showForm && (
            <Card className="mb-8 shadow-lg">
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle className="flex items-center gap-2">
                    <Package className="w-5 h-5" />
                    {editingProduct ? "Modifier le produit" : "Nouveau produit"}
                  </CardTitle>
                  <Button variant="ghost" size="sm" onClick={resetForm}>
                    <X className="w-5 h-5" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Image Upload Section */}
                  <div className="space-y-4">
                    <Label className="text-base font-semibold flex items-center gap-2">
                      <Camera className="w-5 h-5" />
                      Photos du produit
                    </Label>

                    {/* Image Preview Grid */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {uploadedImages.map((img, idx) => (
                        <div
                          key={img || `img-${idx}`}
                          className={`relative group rounded-xl overflow-hidden border-2 ${
                            formData.image_url === img ? 'border-green-500 ring-2 ring-green-500' : 'border-gray-200'
                          }`}
                        >
                          <img
                            src={img}
                            alt={`Product ${idx + 1}`}
                            className="w-full h-32 object-cover"
                          />
                          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                            <Button
                              type="button"
                              size="sm"
                              variant="secondary"
                              className="h-8 w-8 p-0"
                              onClick={() => setMainImage(img)}
                              title="Définir comme image principale"
                            >
                              <Image className="w-4 h-4" />
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              variant="destructive"
                              className="h-8 w-8 p-0"
                              onClick={() => removeImage(idx)}
                              title="Supprimer"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                          {formData.image_url === img && (
                            <div className="absolute top-1 left-1 bg-green-500 text-white text-xs px-2 py-0.5 rounded-full">
                              Principal
                            </div>
                          )}
                        </div>
                      ))}

                      {/* Upload Button */}
                      <div
                        className="relative border-2 border-dashed border-gray-300 rounded-xl h-32 flex flex-col items-center justify-center cursor-pointer hover:border-green-500 hover:bg-green-50 transition-all"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        {uploadingImage ? (
                          <Loader2 className="w-8 h-8 text-green-600 animate-spin" />
                        ) : (
                          <>
                            <Upload className="w-8 h-8 text-gray-400 mb-2" />
                            <span className="text-sm text-gray-500">Ajouter</span>
                          </>
                        )}
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/*"
                          multiple
                          className="hidden"
                          onChange={handleImageUpload}
                          data-testid="image-upload-input"
                        />
                      </div>
                    </div>

                    <p className="text-xs text-muted-foreground">
                      Formats acceptés: JPG, PNG, WebP, GIF (max 5MB par image)
                    </p>
                  </div>

                  {/* Product Details */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">Nom du produit *</Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        placeholder="Ex: Robe traditionnelle"
                        required
                        data-testid="product-name-input"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="category">Catégorie</Label>
                      <select
                        id="category"
                        className="h-10 w-full rounded-lg border border-input bg-background px-3 py-2"
                        value={formData.category}
                        onChange={(e) => setFormData({...formData, category: e.target.value})}
                        data-testid="product-category-select"
                      >
                        <option value="boutique">Boutique</option>
                        <option value="commerce">Commerce</option>
                        <option value="tailor">Couture/Artisan</option>
                        <option value="alimentaire">Alimentaire</option>
                        <option value="electronique">Électronique</option>
                        <option value="autre">Autre</option>
                      </select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="description">Description</Label>
                    <Textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) => setFormData({...formData, description: e.target.value})}
                      placeholder="Description détaillée du produit..."
                      rows={3}
                      data-testid="product-description-input"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="price">Prix (FCFA) *</Label>
                      <Input
                        id="price"
                        type="number"
                        value={formData.price}
                        onChange={(e) => setFormData({...formData, price: e.target.value})}
                        placeholder="5000"
                        required
                        data-testid="product-price-input"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="stock">Stock disponible</Label>
                      <Input
                        id="stock"
                        type="number"
                        value={formData.stock}
                        onChange={(e) => setFormData({...formData, stock: e.target.value})}
                        placeholder="10"
                        data-testid="product-stock-input"
                      />
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      className="flex-1 rounded-full"
                      onClick={resetForm}
                    >
                      Annuler
                    </Button>
                    <Button
                      type="submit"
                      className="flex-1 bg-green-600 hover:bg-green-700 rounded-full"
                      disabled={isLoading}
                      data-testid="save-product-btn"
                    >
                      {isLoading ? (
                        <Loader2 className="w-5 h-5 animate-spin mr-2" />
                      ) : (
                        <Save className="w-5 h-5 mr-2" />
                      )}
                      {editingProduct ? "Mettre à jour" : "Enregistrer"}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          )}

          {/* Products List */}
          <Card className="shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package className="w-5 h-5" />
                Catalogue ({products.length} produits)
              </CardTitle>
            </CardHeader>
            <CardContent>
              {isLoading && products.length === 0 ? (
                <div className="text-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin mx-auto text-green-600" />
                  <p className="text-muted-foreground mt-2">Chargement...</p>
                </div>
              ) : products.length === 0 ? (
                <div className="text-center py-12">
                  <Package className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                  <p className="text-muted-foreground mb-4">Aucun produit dans votre catalogue</p>
                  <Button
                    onClick={() => setShowForm(true)}
                    className="bg-green-600 hover:bg-green-700 rounded-full"
                  >
                    <Plus className="w-5 h-5 mr-2" />
                    Ajouter votre premier produit
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {products.map((product) => (
                    <div
                      key={product.id}
                      className="border rounded-xl overflow-hidden bg-white hover:shadow-md transition-all"
                      data-testid={`product-card-${product.id}`}
                    >
                      {/* Product Image */}
                      <div className="relative h-48 bg-gray-100">
                        {product.image_url ? (
                          <img
                            src={product.image_url}
                            alt={product.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Image className="w-16 h-16 text-gray-300" />
                          </div>
                        )}
                        {product.images && product.images.length > 1 && (
                          <div className="absolute bottom-2 right-2 bg-black/60 text-white text-xs px-2 py-1 rounded-full">
                            +{product.images.length - 1} photos
                          </div>
                        )}
                      </div>

                      {/* Product Info */}
                      <div className="p-4">
                        <h3 className="font-bold text-lg mb-1">{product.name}</h3>
                        <p className="text-sm text-muted-foreground line-clamp-2 mb-2">
                          {product.description}
                        </p>
                        <div className="flex justify-between items-center mb-3">
                          <span className="text-xl font-bold text-green-600">
                            {product.price.toLocaleString()} FCFA
                          </span>
                          <span className="text-sm text-muted-foreground">
                            Stock: {product.stock}
                          </span>
                        </div>

                        {/* Actions */}
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            className="flex-1 rounded-full"
                            onClick={() => handleEdit(product)}
                            data-testid={`edit-product-${product.id}`}
                          >
                            <Edit className="w-4 h-4 mr-1" />
                            Modifier
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="rounded-full text-red-600 hover:bg-red-50"
                            onClick={() => handleDelete(product.id)}
                            data-testid={`delete-product-${product.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
